/**
 * Newsletter Popup Controller
 * Manages the display timing and state of the newsletter popup
 */
class NewsletterPopupController {
  constructor() {
    this.popup = document.querySelector('#newsletter-popup');
    this.subscribedKey = 'newsletter_subscribed';
    this.sessionKey = 'newsletter_popup_last_shown';
    this.sessionTimeout = 30 * 60 * 1000; // 30 minutes in milliseconds
    this.delay = this.getDelay();

    if (this.shouldShowPopup()) {
      this.schedulePopup();
    }

    this.attachEventListeners();
  }

  /**
   * Get delay from data attribute or default to 10 seconds
   */
  getDelay() {
    const delayAttr = this.popup?.getAttribute('data-delay');
    return delayAttr ? parseInt(delayAttr) * 1000 : 10000;
  }

  /**
   * Check if popup should be shown based on session and subscription state
   */
  shouldShowPopup() {
    if (!this.popup) return false;

    // Check if user has subscribed (permanent)
    if (localStorage.getItem(this.subscribedKey) === 'true') {
      return false;
    }

    // Check if popup was shown in current session (30-min timeout)
    const lastShown = localStorage.getItem(this.sessionKey);
    if (lastShown) {
      const timeSinceShown = Date.now() - parseInt(lastShown);
      if (timeSinceShown < this.sessionTimeout) {
        return false; // Still within session window
      }
    }

    return true;
  }

  /**
   * Mark popup as shown in this session
   */
  markAsShown() {
    localStorage.setItem(this.sessionKey, Date.now().toString());
  }

  /**
   * Schedule the popup to appear after delay
   */
  schedulePopup() {
    setTimeout(() => {
      this.showPopup();
    }, this.delay);
  }

  /**
   * Show the popup
   */
  showPopup() {
    if (this.popup) {
      const dialogComponent = this.popup;
      if (dialogComponent && typeof dialogComponent.showDialog === 'function') {
        dialogComponent.showDialog();
        // Mark as shown immediately when popup appears
        this.markAsShown();
      }
    }
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    if (!this.popup) return;

    // After form posts back successfully, Shopify rerenders the success node.
    // Re-open the popup so the user sees the confirmation, and persist subscribed state.
    if (this.popup.querySelector('[data-newsletter-success]')) {
      localStorage.setItem(this.subscribedKey, 'true');
      localStorage.removeItem(this.sessionKey);
      if (typeof this.popup.showDialog === 'function') {
        this.popup.showDialog();
      }
    }

    // Listen for dialog close (via close button or "No thanks")
    const dialog = this.popup.querySelector('dialog');
    if (dialog) {
      dialog.addEventListener('close', () => {
        if (localStorage.getItem(this.subscribedKey) !== 'true') {
          this.markAsShown();
        }
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new NewsletterPopupController();
  });
} else {
  new NewsletterPopupController();
}
